import { memo, useState, useRef, useEffect } from "react";

import classnames from "classnames";
import { capitalize, isNotEmpty } from "neetocist";
import usePrevious from "neetocommons/react-utils/usePrevious";
import { Button, Toastr, Typography, Spinner } from "neetoui";
import PropTypes from "prop-types";
import { fromPairs, isEmpty, prop } from "ramda";
import { Trans, useTranslation } from "react-i18next";

import ChatWidgetBody from "./ChatWidgetBody";
import CodeBlock from "./CodeBlock";
import CodeSnippet from "./CodeSnippet";
import {
  EMAIL_TYPES,
  SAMPLE_CONTEXT_CODE_STRING,
  INSTALLATION_HELP_EMAIL,
  FRAMEWORK_SELECT_OPTIONS,
  NEETO_CHAT_USER_IDENTITY_URL,
  NEETO_REPLAY_CONFIGURE_URL,
  WIDGET_TYPES,
  WIDGET_KB_HELP_URL,
  USE_NEETO_WIDGET_NPM_PAGE_URL,
} from "./constants";
import useForceUpdate from "./hooks/useForceUpdate";
import {
  useFetchApiKey,
  useFetchConfigurations,
  useUpdateConfigurations,
} from "./queries";
import SelectionSwitches from "./SelectionSwitches";
import {
  getSampleUserIdentity,
  getSortedPrefixedWidgets,
  getWidgetDescriptions,
  sampleConsolidatedCodeString,
  shouldShowSwitches,
} from "./utils";

const EmbedCode = ({
  showSessionContext,
  showUserIdentity,
  primaryApp = "",
  maxWidth = "max-w-3xl",
}) => {
  const [emailType, setEmailType] = useState(EMAIL_TYPES.null);
  const [selectedFramework, setSelectedFramework] = useState(
    FRAMEWORK_SELECT_OPTIONS[0]
  );

  const { t } = useTranslation();
  const bodyRef = useRef();
  const forceUpdate = useForceUpdate();

  useEffect(() => {
    forceUpdate();
  }, []);

  const { data: { widgetApiKey = "" } = {}, isLoading: isLoadingApiKey } =
    useFetchApiKey();

  const {
    data: { neetoWidgetTokens = [] } = {},
    isLoading: isLoadingConfigurations,
  } = useFetchConfigurations();

  const { mutate: updateWidgetConfiguration } = useUpdateConfigurations();

  const previousEmailType = usePrevious(emailType);

  const selectedWidgets = [primaryApp];

  const isOnlyChatWidgetSelected =
    selectedWidgets.length === 1 && selectedWidgets[0] === WIDGET_TYPES.chat;

  const enabledClientApps = neetoWidgetTokens
    .filter(prop("clientApplicationEnabled"))
    .map(item => item.clientApplicationName.toLowerCase());

  const enabledWidgets = fromPairs(
    selectedWidgets.map(widget => [widget, true])
  );

  const shouldShowUserIdentity =
    showUserIdentity ?? (enabledWidgets.chat || enabledWidgets.replay);

  const shouldShowSessionContext = showSessionContext ?? enabledWidgets.replay;

  const updateSelectedWidgets = widget => {
    const widgetId = neetoWidgetTokens.find(
      item => item.clientApplicationName.toLowerCase() === widget
    ).id;
    const isEnabled = !selectedWidgets.includes(widget);

    updateWidgetConfiguration(
      { id: widgetId, payload: { enabled: isEnabled } },
      {
        onSuccess: () => {
          const productName = `neeto${capitalize(widget)}`;
          const response = isEnabled
            ? "neetoMolecules.widget.installation.enabled"
            : "neetoMolecules.widget.installation.disabled";

          Toastr.success(t(response, { productName }));
        },
      }
    );
  };

  const getEmbedCodeString = () =>
    isNotEmpty(widgetApiKey)
      ? sampleConsolidatedCodeString(enabledWidgets, widgetApiKey)
      : "";

  const getSnippetProps = selectedEmailType => {
    switch (selectedEmailType) {
      case EMAIL_TYPES.embedCode:
        return {
          codeString: getEmbedCodeString(),
          subject: t(
            "neetoMolecules.widget.email.fields.subject.values.embedCode",
            { widgetList: getSortedPrefixedWidgets(selectedWidgets) }
          ),
          body: (
            <div ref={bodyRef}>
              <Trans
                i18nKey="neetoMolecules.widget.email.body.values.embedCode.mainText"
                components={{
                  p: <Typography className="mb-2" style="body2" />,
                }}
                values={{
                  val: getWidgetDescriptions(selectedWidgets),
                }}
              />
              <ChatWidgetBody widgets={selectedWidgets} />
              <Trans
                i18nKey="neetoMolecules.widget.email.body.values.embedCode.supportText"
                values={{ helpEmail: INSTALLATION_HELP_EMAIL }}
                components={{
                  p: <Typography className="mb-2" style="body2" />,
                  mail: (
                    <Button
                      href={`mailto:${INSTALLATION_HELP_EMAIL}`}
                      rel="noreferrer"
                      style="link"
                      target="_blank"
                    />
                  ),
                }}
              />
            </div>
          ),
        };
      case EMAIL_TYPES.userIdentity:
        return {
          emailType: EMAIL_TYPES.userIdentity,
          enabledWidgets,
          subject: t(
            "neetoMolecules.widget.email.fields.subject.values.userIdentity"
          ),
          body: (
            <div ref={bodyRef}>
              <Trans
                i18nKey="neetoMolecules.widget.email.body.values.userIdentity"
                components={{
                  p: <Typography className="mb-2" style="body2" />,
                  configureLink: (
                    <Button
                      rel="noreferrer"
                      style="link"
                      target="_blank"
                      href={
                        enabledWidgets.chat
                          ? NEETO_CHAT_USER_IDENTITY_URL
                          : NEETO_REPLAY_CONFIGURE_URL
                      }
                    />
                  ),
                }}
                values={{
                  userIdentityKbUrl: enabledWidgets.chat
                    ? NEETO_CHAT_USER_IDENTITY_URL
                    : NEETO_REPLAY_CONFIGURE_URL,
                }}
              />
            </div>
          ),
        };
      case EMAIL_TYPES.sessionContext:
        return {
          codeString: SAMPLE_CONTEXT_CODE_STRING,
          subject: t(
            "neetoMolecules.widget.email.fields.subject.values.sessionContext"
          ),
          body: (
            <div ref={bodyRef}>
              <Trans
                i18nKey="neetoMolecules.widget.email.body.values.sessionContext"
                values={{ sessionContextKbUrl: NEETO_REPLAY_CONFIGURE_URL }}
                components={{
                  p: <Typography className="mb-2" style="body2" />,
                  configureLink: (
                    <Button
                      href={NEETO_REPLAY_CONFIGURE_URL}
                      rel="noreferrer"
                      style="link"
                      target="_blank"
                    />
                  ),
                }}
              />
            </div>
          ),
        };
      case EMAIL_TYPES.null:
        return previousEmailType !== EMAIL_TYPES.null
          ? getSnippetProps(previousEmailType)
          : {};
      default:
        throw new Error(t("neetoMolecules.widget.email.invalidType"));
    }
  };

  const handleSendViaEmail = selectedEmail => {
    if (isEmpty(selectedWidgets)) {
      Toastr.error(
        t("neetoMolecules.widget.installation.snippet.disabledError")
      );

      return;
    }

    setEmailType(selectedEmail);
  };

  const getSelectedWidgetsInstructionsKey = () => {
    if (isEmpty(selectedWidgets)) {
      return "neetoMolecules.widget.installation.instructions.placement.noneOfThese";
    }

    if (isOnlyChatWidgetSelected) {
      return "neetoMolecules.widget.installation.instructions.placement.selectedChatWidget";
    }

    return "neetoMolecules.widget.installation.instructions.placement.selectedWidgets";
  };

  const embedHelpTextTranslationComponents = {
    fontBold: <Typography component="span" style="body2" weight="semibold" />,
    widgetNpmLink: (
      <Button
        href={USE_NEETO_WIDGET_NPM_PAGE_URL}
        rel="noreferrer"
        style="link"
        target="_blank"
      />
    ),
    Link: (
      <Button
        href={WIDGET_KB_HELP_URL[primaryApp]}
        rel="noreferrer"
        style="link"
        target="_blank"
      />
    ),
  };

  if (isLoadingApiKey || isLoadingConfigurations) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-auto mb-10 w-full flex-col items-center justify-start">
      <div className={classnames("mx-auto", maxWidth)}>
        {shouldShowSwitches() && (
          <SelectionSwitches
            {...{
              enabledClientApps,
              primaryApp,
              selectedWidgets,
              updateSelectedWidgets,
            }}
          />
        )}
        <div className="mb-8 w-full flex-grow flex-col items-center justify-start">
          <div className="mx-auto mb-2 w-full" data-testid="embed-code-block">
            <CodeBlock
              className="neeto-ui-rounded-lg"
              codeString={getEmbedCodeString()}
              sendViaEmail={() => handleSendViaEmail(EMAIL_TYPES.embedCode)}
              title={<div>{t("neetoMolecules.widget.title.embedCode")}</div>}
            />
          </div>
          <div className="w-full">
            <Trans
              components={embedHelpTextTranslationComponents}
              i18nKey={getSelectedWidgetsInstructionsKey()}
              values={{ widgetList: getSortedPrefixedWidgets(selectedWidgets) }}
            />
          </div>
        </div>
        {shouldShowUserIdentity ? (
          <div className="mb-8 w-full flex-grow flex-col items-center justify-start">
            <div className="mx-auto mb-2 w-full">
              <CodeBlock
                className="neeto-ui-rounded-lg"
                frameworkProps={{ selectedFramework, setSelectedFramework }}
                title={t("neetoMolecules.widget.title.userIdentity")}
                codeString={getSampleUserIdentity(
                  enabledWidgets,
                  selectedFramework.value
                )}
                sendViaEmail={() =>
                  handleSendViaEmail(EMAIL_TYPES.userIdentity)
                }
              />
            </div>
            <Trans
              components={{
                p: <Typography style="body2" />,
                Link: (
                  <Button
                    rel="noreferrer"
                    style="link"
                    target="_blank"
                    href={
                      enabledWidgets.chat
                        ? NEETO_CHAT_USER_IDENTITY_URL
                        : NEETO_REPLAY_CONFIGURE_URL
                    }
                  />
                ),
              }}
              i18nKey={
                enabledWidgets.chat
                  ? "neetoMolecules.widget.installation.instructions.userIdentity.chat"
                  : "neetoMolecules.widget.installation.instructions.userIdentity.replay"
              }
            />
          </div>
        ) : null}
        {shouldShowSessionContext ? (
          <div className="w-full flex-grow flex-col items-center justify-start">
            <div className="mx-auto mb-2 w-full">
              <CodeBlock
                className="neeto-ui-rounded-lg"
                codeString={SAMPLE_CONTEXT_CODE_STRING}
                title={t("neetoMolecules.widget.title.sessionContext")}
                sendViaEmail={() =>
                  handleSendViaEmail(EMAIL_TYPES.sessionContext)
                }
              />
            </div>
            <Trans
              i18nKey="neetoMolecules.widget.installation.instructions.sessionContext"
              components={{
                p: <Typography style="body2" />,
                Link: (
                  <Button
                    href={NEETO_REPLAY_CONFIGURE_URL}
                    rel="noreferrer"
                    style="link"
                    target="_blank"
                  />
                ),
              }}
            />
          </div>
        ) : null}
      </div>
      <CodeSnippet
        isPaneOpen={!!emailType && isNotEmpty(selectedWidgets)}
        onClose={() => setEmailType(EMAIL_TYPES.null)}
        {...getSnippetProps(emailType)}
      />
    </div>
  );
};

EmbedCode.propTypes = {
  primaryApp: PropTypes.string,
  maxWidth: PropTypes.string,
};

export default memo(EmbedCode);
